import React, { lazy, Suspense, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import Loading from '../components/shared-components/loading';
import Routes from '../routes';
import { RootContext } from '../context/RootContext';
import { Card, Col, ConfigProvider, Row, Skeleton } from 'antd';
import { resources } from '../lang';
import { darkTheme, lightTheme } from '../configs/ThemeConfig';
import {
  PUBLIC_PATH_PREFIX,
  AUTH_PATH_PREFIX,
  APP_PATH_PREFIX,
} from '../configs/AppConfig';
import ErrorBoundary from '../components/shared-components/Errorboundary';

const AppLayout = lazy(() => import('./AppLayout'));
const AuthLayout = lazy(() => import('./AuthLayout'));
const PublicLayout = lazy(() => import('./PublicLayout'));

const LoadingFallback = () => {
  return (
    <div style={{ padding: 24 }}>
      {/* --- KPI Skeletons --- */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Col xs={12} md={6} key={i}>
            <Card>
              <Skeleton
                active
                // avatar
                // round
                paragraph={{ rows: 2 }}
                style={{
                  borderRadius: 12,
                  background: 'rgba(0,0,0,0.03)',
                }}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* --- Table Skeleton --- */}
      <Card>
        <Skeleton
          active
          paragraph={{ rows: 8 }}
          style={{
            borderRadius: 12,
            background: 'rgba(0,0,0,0.03)',
          }}
        />
      </Card>
    </div>
  );
};

const Layouts = () => {
  const ctxValue = useContext(RootContext);
  const location = useLocation();
  const currentAppLocale = resources['en'];

  // Determine which layout to use based on the current path
  const getLayout = () => {
    const pathname = location.pathname;

    // Public routes - use PublicLayout (minimal, no header/nav) regardless of auth status
    if (pathname.startsWith(PUBLIC_PATH_PREFIX)) {
      return PublicLayout;
    }

    // Auth routes - use AuthLayout (minimal layout)
    if (pathname.startsWith(AUTH_PATH_PREFIX)) {
      return AuthLayout;
    }

    // App routes - use AppLayout (with header/nav) but only if authenticated
    if (pathname.startsWith(APP_PATH_PREFIX)) {
      return ctxValue?.token ? AppLayout : AuthLayout;
    }

    // Default: use AppLayout if authenticated, otherwise AuthLayout
    return ctxValue?.token ? AppLayout : AuthLayout;
  };

  const Layout = getLayout();
  const themeConfig =
    ctxValue.theme === 'light' ? { ...lightTheme } : { ...darkTheme };

  return (
    <ErrorBoundary>
      <ConfigProvider
        direction={ctxValue.direction}
        locale={currentAppLocale.antd}
        theme={themeConfig}
      >
        <Suspense fallback={<Loading cover="content" theme={ctxValue.theme} />}>
          <Layout>
            <Routes />
          </Layout>
        </Suspense>
      </ConfigProvider>
    </ErrorBoundary>
  );
};
export default React.memo(Layouts);
